import type { Permission } from '@takumi/shared';
import { ROLE_LABELS } from '@takumi/shared';
import {
  BarChart3, Boxes, CalendarCheck, ChevronDown, Columns3, FileText, Inbox, KeyRound, LogOut, Menu, Settings, ShoppingBag, Store, Sun, Users, X,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useAuth } from '@/lib/auth';
import { cn, initials } from '@/lib/utils';
import { ErrorBoundary } from './ErrorBoundary';
import { Logo } from './Logo';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/primitives';
import { useNavCounts } from '@/features/shell/useNavCounts';
import { GlobalSearch } from '@/features/shell/GlobalSearch';
import { NotificationBell } from '@/features/shell/NotificationBell';
import { NewLeadButton } from '@/features/organizations/NewLeadDialog';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  permission?: Permission;
  count?: number;
  countTone?: 'alert' | 'neutral';
  end?: boolean;
}

export function AppShell() {
  const { me, logout, can } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const counts = useNavCounts();
  const location = useLocation();

  const sections: { title?: string; items: NavItem[] }[] = [
    {
      items: [
        { to: '/', label: 'היום שלי', icon: <Sun />, end: true, count: counts?.overdueFollowups, countTone: 'alert' },
        { to: '/inbox', label: 'פניות נכנסות', icon: <Inbox />, count: counts?.pendingReviews, countTone: 'alert' },
      ],
    },
    {
      title: 'מכירות',
      items: [
        { to: '/leads', label: 'לידים', icon: <Users />, count: counts?.openLeads, countTone: 'neutral' },
        { to: '/pipeline', label: 'פייפליין', icon: <Columns3 /> },
        { to: '/customers', label: 'לקוחות', icon: <Store /> },
        { to: '/tasks', label: 'משימות ופולואפים', icon: <CalendarCheck /> },
      ],
    },
    {
      title: 'מסחר',
      items: [
        { to: '/quotes', label: 'הצעות מחיר', icon: <FileText />, permission: 'sales.read' },
        { to: '/orders', label: 'הזמנות', icon: <ShoppingBag />, permission: 'sales.read' },
        { to: '/products', label: 'מוצרים ומלאי', icon: <Boxes />, permission: 'sales.read' },
      ],
    },
    {
      title: 'ניהול',
      items: [
        { to: '/reports', label: 'דוחות', icon: <BarChart3 />, permission: 'reports.read' },
        { to: '/settings', label: 'הגדרות', icon: <Settings />, permission: 'crm.read' },
      ],
    },
  ];

  const nav = (
    <nav className="flex flex-1 flex-col gap-5 overflow-y-auto px-3 py-4" aria-label="ניווט ראשי">
      {sections.map((s, i) => (
        <div key={i}>
          {s.title && <div className="mb-1.5 px-3 text-[11px] font-bold tracking-wider text-matcha-400">{s.title}</div>}
          <ul className="flex flex-col gap-0.5">
            {s.items
              .filter((it) => !it.permission || can(it.permission))
              .map((it) => (
                <li key={it.to}>
                  <NavLink
                    to={it.to}
                    end={it.end}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'group flex items-center gap-3 rounded-control px-3 py-2 text-[14.5px] font-medium text-matcha-100/85 transition-colors',
                        '[&>svg]:size-[18px] [&>svg]:shrink-0 [&>svg]:text-matcha-400',
                        'hover:bg-white/5 hover:text-white',
                        isActive && 'bg-white/10 text-white [&>svg]:text-gold-400',
                      )
                    }
                  >
                    {it.icon}
                    <span className="flex-1">{it.label}</span>
                    {!!it.count && (
                      <span
                        className={cn(
                          'min-w-5 rounded-full px-1.5 text-center text-[11px] leading-5 font-bold tabular',
                          it.countTone === 'alert' ? 'bg-overdue text-white' : 'bg-white/10 text-matcha-100',
                        )}
                      >
                        {it.count > 99 ? '99+' : it.count}
                      </span>
                    )}
                  </NavLink>
                </li>
              ))}
          </ul>
        </div>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-screen">
      {/* Sidebar (right side in RTL) */}
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col bg-matcha-900 lg:flex">
        <div className="px-5 pt-5 pb-3">
          <Logo inverted />
        </div>
        {nav}
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal>
          <div className="absolute inset-0 bg-matcha-950/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 start-0 flex w-64 flex-col bg-matcha-900 animate-fade-in">
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <Logo inverted />
              <button onClick={() => setMobileOpen(false)} className="text-matcha-200" aria-label="סגירת תפריט">
                <X />
              </button>
            </div>
            {nav}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-line bg-paper/90 px-4 backdrop-blur lg:px-6">
          <button className="lg:hidden" onClick={() => setMobileOpen(true)} aria-label="פתיחת תפריט">
            <Menu className="size-5" />
          </button>
          <GlobalSearch key={location.pathname} />
          <div className="ms-auto flex items-center gap-2">
            {can('crm.write') && <NewLeadButton />}
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 rounded-control px-1.5 py-1 hover:bg-sunken" aria-label="תפריט משתמש">
                <span className="grid size-8 place-items-center rounded-full bg-matcha-700 text-xs font-bold text-white">{initials(me?.user.fullName)}</span>
                <span className="hidden text-sm font-semibold md:block">{me?.user.fullName}</span>
                <ChevronDown className="hidden size-4 text-muted md:block" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>
                  {me?.user.email}
                  <div className="font-normal">{me && ROLE_LABELS[me.user.role]}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <NavLink to="/settings/account">
                    <KeyRound /> החשבון שלי
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void logout()}>
                  <LogOut /> התנתקות
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="min-w-0 flex-1 px-4 py-5 lg:px-8 lg:py-7">
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-[26px] leading-tight font-semibold">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
